import * as React from 'react';
import MuiDialog, { type DialogProps as MuiDialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

export interface DialogProps extends Omit<MuiDialogProps, 'open' | 'title'> {
  /** react-toolbox: whether dialog is visible */
  active?: boolean;
  /** react-toolbox: dialog title */
  title?: React.ReactNode;
  /** react-toolbox: action buttons */
  actions?: React.ReactNode[];
  /** react-toolbox: callback when Escape key is pressed */
  onEscKeyDown?: () => void;
  /** react-toolbox: callback when overlay/backdrop is clicked */
  onOverlayClick?: () => void;
  /** react-toolbox: additional class for the dialog */
  className?: string;
  /** MUI open prop (takes precedence over active) */
  open?: boolean;
  /** react-toolbox: dialog type (ignored, for compatibility) */
  type?: string;
}

/**
 * Dialog wrapper - maps react-toolbox Dialog to MUI Dialog
 * - `active` maps to `open`
 * - `onEscKeyDown` and `onOverlayClick` are combined into `onClose`
 * - `title` renders in DialogTitle
 * - `actions` render in DialogActions
 * - children render in DialogContent
 */
const Dialog = React.forwardRef<HTMLDivElement, DialogProps>(
  (
    {
      active,
      open: openProp,
      title,
      actions,
      onEscKeyDown,
      onOverlayClick,
      onClose,
      children,
      type: _type, // ignored, for compatibility
      ...rest
    },
    ref,
  ) => {
    const isOpen = openProp !== undefined ? openProp : (active ?? false);

    const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'escapeKeyDown' && onEscKeyDown) {
        onEscKeyDown();
      } else if (reason === 'backdropClick' && onOverlayClick) {
        onOverlayClick();
      }

      // Also call MUI's onClose if provided
      if (onClose) {
        onClose(event, reason);
      }
    };

    return (
      <MuiDialog ref={ref} open={isOpen} onClose={handleClose} {...rest}>
        {title && <DialogTitle>{title}</DialogTitle>}
        {children && <DialogContent>{children}</DialogContent>}
        {actions && actions.length > 0 && <DialogActions>{actions}</DialogActions>}
      </MuiDialog>
    );
  },
);

Dialog.displayName = 'Dialog';

export default Dialog;
